import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import MetricCard from '../shared/MetricCard';
import SectionHeading from '../shared/SectionHeading';
import CardGridSkeleton from '../shared/skeletons/CardGridSkeleton';

import formatters from '../shared/formatters';

const cardGridColumns = {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))'};

// Unconfirmed hours only exist when both sides of the subtraction are present;
// otherwise the value is missing, not zero.
const getUnconfirmedHours = (totals) => {
  if (!totals || formatters.isMissingValue(totals.totalHours) || formatters.isMissingValue(totals.confirmedHours)) {
    return null;
  }

  return totals.totalHours - totals.confirmedHours;
};

const FinancialOverviewSection = (props) => {
  const {
    isLoading,
    currentSnapshot,
    previousSnapshot,
  } = props;

  const forecastCards = React.useMemo(() => {
    const currentTotals = currentSnapshot?.forecast?.totals || null;
    const previousTotals = previousSnapshot?.forecast?.totals || null;
    const buildCard = (key, labelExpression, comparisonOptions) => ({
      key,
      label: translate(labelExpression),
      comparison: formatters.buildComparison(
          currentTotals?.[key],
          previousTotals?.[key],
          comparisonOptions,
      ),
    });

    return [
      buildCard('scheduledHours', 'metrics.ClientMetrics.FinancialOverview.ScheduledHours', {kind: 'hours', goodDirection: 'up'}),
      buildCard('weightedHours', 'metrics.ClientMetrics.FinancialOverview.WeightedHours', {kind: 'hours', goodDirection: 'up'}),
      buildCard('revenue', 'metrics.ClientMetrics.FinancialOverview.ForecastRevenue', {kind: 'currency', goodDirection: 'up'}),
      buildCard('laborCost', 'metrics.ClientMetrics.FinancialOverview.ForecastLabor', {kind: 'currency', goodDirection: 'down'}),
      buildCard('grossProfit', 'metrics.ClientMetrics.FinancialOverview.ForecastGrossProfit', {kind: 'currency', goodDirection: 'up'}),
      buildCard('grossMargin', 'metrics.ClientMetrics.FinancialOverview.ForecastGrossMargin', {kind: 'margin', goodDirection: 'up'}),
    ];
  }, [currentSnapshot, previousSnapshot]);

  const actualCards = React.useMemo(() => {
    const currentTotals = currentSnapshot?.actual?.totals || null;
    const previousTotals = previousSnapshot?.actual?.totals || null;
    const buildCard = (key, labelExpression, comparisonOptions) => ({
      key,
      label: translate(labelExpression),
      comparison: formatters.buildComparison(
          currentTotals?.[key],
          previousTotals?.[key],
          comparisonOptions,
      ),
    });

    return [
      buildCard('confirmedHours', 'metrics.ClientMetrics.FinancialOverview.ConfirmedHours', {kind: 'hours', goodDirection: 'up'}),
      {
        key: 'unconfirmedHours',
        label: translate('metrics.ClientMetrics.FinancialOverview.UnconfirmedHours'),
        comparison: formatters.buildComparison(
            getUnconfirmedHours(currentTotals),
            getUnconfirmedHours(previousTotals),
            {kind: 'hours', goodDirection: 'down'},
        ),
      },
      buildCard('actualRevenue', 'metrics.ClientMetrics.FinancialOverview.ActualRevenue', {kind: 'currency', goodDirection: 'up'}),
      buildCard('actualLaborCost', 'metrics.ClientMetrics.FinancialOverview.ActualLabor', {kind: 'currency', goodDirection: 'down'}),
      buildCard('grossProfit', 'metrics.ClientMetrics.FinancialOverview.ActualGrossProfit', {kind: 'currency', goodDirection: 'up'}),
      buildCard('grossMargin', 'metrics.ClientMetrics.FinancialOverview.ActualGrossMargin', {kind: 'margin', goodDirection: 'up'}),
    ];
  }, [currentSnapshot, previousSnapshot]);

  const forecastSubtitle = translate('metrics.ClientMetrics.FinancialOverview.ForecastSubtitle', {
    startDate: formatters.formatIsoDate(currentSnapshot?.forecastStart),
    endDate: formatters.formatIsoDate(currentSnapshot?.forecastEnd),
  });
  const actualSubtitle = translate('metrics.ClientMetrics.FinancialOverview.ActualSubtitle', {
    date: formatters.formatWeekOfDate(currentSnapshot?.actualThroughWeek),
  });

  const renderCardGroup = (title, subtitle, cards) => (
    <Box>
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 1.5,
        }}
      >
        {subtitle}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: cardGridColumns,
          gap: 1.75,
        }}
      >
        {
          cards.map((card) => (
            <MetricCard
              key={card.key}
              label={card.label}
              value={card.comparison.value}
              previousValue={card.comparison.previous}
              chip={card.comparison.chip}
            />
          ))
        }
      </Box>
    </Box>
  );

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.FinancialOverview.SectionTitle' />
      {
        isLoading &&
        <Stack spacing={2.5}>
          <CardGridSkeleton cardCount={6} gridTemplateColumns={cardGridColumns} />
          <CardGridSkeleton cardCount={6} gridTemplateColumns={cardGridColumns} />
        </Stack>
      }
      {
        !isLoading &&
        <Stack spacing={2.5}>
          {renderCardGroup(translate('metrics.ClientMetrics.FinancialOverview.ForecastTitle'), forecastSubtitle, forecastCards)}
          {renderCardGroup(translate('metrics.ClientMetrics.FinancialOverview.ActualTitle'), actualSubtitle, actualCards)}
        </Stack>
      }
    </Box>
  );
};

FinancialOverviewSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
  previousSnapshot: PropTypes.object,
};

export default React.memo(FinancialOverviewSection);
